/**
 * Logik der Enemy View (Masterprompt Abschnitte 83, 84).
 *
 * Die Enemy View zeigt die Lane des Gegners. Der eigene Hero bleibt dabei
 * stehen, greift aber automatisch an und nimmt Schaden. Damit man die eigene
 * Line nicht aus den Augen verliert, warnt die Oberflaeche, sobald sie unter
 * Druck geraet - springt aber NICHT von selbst zurueck (Abschnitt 84).
 *
 * Wann ist die eigene Line "kritisch"? Das legt der Masterprompt nicht fest.
 * Die Grenzwerte unten sind deshalb TUNING_PLACEHOLDER und koennen jederzeit
 * nachjustiert werden.
 */

import { getLanePathLength, projectOntoLane } from '@dueline/game-rules';

export const LINE_PRESSURE = {
  /**
   * Ab diesem Anteil der Lane gilt ein Creep als "kurz vor dem Portal".
   * 0.7 heisst: Er hat mehr als zwei Drittel des Weges hinter sich.
   */
  nearPortalProgress: 0.7,
  /** So lange nach einem Portaltreffer gilt die Line als kritisch, in Millisekunden. */
  portalDamageWindowMs: 6000,
  /** Unter diesem HP-Anteil gilt der eigene Hero als angeschlagen. */
  heroLowRatio: 0.3,
} as const;

/** Warum die eigene Line gerade kritisch ist. */
export type PressureReason = 'portal_damage' | 'creeps_near_portal' | 'hero_down';

export interface LinePressureInput {
  /** Millisekunden seit dem letzten Portaltreffer; null = noch nie getroffen. */
  msSincePortalDamage: number | null;
  /** Gegnerische Creeps in der eigenen Lane kurz vor dem Portal. */
  creepsNearPortal: number;
  /** Alle gegnerischen Creeps in der eigenen Lane. */
  creepsInLane: number;
  heroAlive: boolean;
  /** HP des eigenen Heroes, 0 bis 1. */
  heroHpRatio: number;
}

export interface LinePressure {
  critical: boolean;
  reasons: PressureReason[];
}

/**
 * Ist die eigene Line gerade unter Druck?
 *
 * Kritisch ist sie, wenn
 * - das Portal gerade getroffen wurde,
 * - ein Creep kurz vor dem Portal steht,
 * - oder der eigene Hero tot bzw. stark angeschlagen ist, WAEHREND Creeps in
 *   der Lane sind. Ein angeschlagener Hero in leerer Lane ist kein Notfall.
 */
export function assessLinePressure(input: LinePressureInput): LinePressure {
  const reasons: PressureReason[] = [];

  if (
    input.msSincePortalDamage !== null &&
    input.msSincePortalDamage < LINE_PRESSURE.portalDamageWindowMs
  ) {
    reasons.push('portal_damage');
  }

  if (input.creepsNearPortal > 0) reasons.push('creeps_near_portal');

  const heroDown = !input.heroAlive || input.heroHpRatio < LINE_PRESSURE.heroLowRatio;
  if (heroDown && input.creepsInLane > 0) reasons.push('hero_down');

  return { critical: reasons.length > 0, reasons };
}

/** Anteil des Weges, den ein Creep auf der Lane zurueckgelegt hat, 0 bis 1. */
export function getLaneProgress(position: { x: number; y: number }): number {
  const length = getLanePathLength();
  if (length <= 0) return 0;
  return Math.min(1, Math.max(0, projectOntoLane(position).travelled / length));
}

/** Zaehlt die Creeps einer Lane, die kurz vor dem Portal stehen. */
export function countCreepsNearPortal(
  creeps: Iterable<{ laneOwner: string; x: number; y: number; hp: number }>,
  laneOwner: string,
): number {
  let count = 0;
  for (const creep of creeps) {
    if (creep.laneOwner !== laneOwner || creep.hp <= 0) continue;
    if (getLaneProgress(creep) >= LINE_PRESSURE.nearPortalProgress) count += 1;
  }
  return count;
}

/**
 * "Ungefaehre HP" des Gegners (Abschnitt 83): gerundet auf volle 10 Prozent.
 * Ein noch lebender Hero zeigt nie 0 - sonst saehe er tot aus.
 */
export function approximateRatio(ratio: number, alive = true): number {
  if (!Number.isFinite(ratio) || ratio <= 0) return 0;
  const gerundet = Math.round(Math.min(1, ratio) * 10) / 10;
  return alive && gerundet === 0 ? 0.1 : gerundet;
}
